// 唯一与后端通信的模块。组件永远不直接 fetch，
// 这样换后端、加认证、加重试都只改这一个文件。

// API 地址的三级回退（优先级从高到低）：
//   1. public/config.json 的 apiBaseUrl —— 运行时可改，换后端无需重新构建
//   2. 构建期注入的 VITE_API_BASE_URL —— 适合 CI 固定环境
//   3. 同源 /api/v1 —— 本地开发走 Vite 代理
const BUILD_TIME_BASE = import.meta.env.VITE_API_BASE_URL ?? ''
const FALLBACK_BASE = '/api/v1'

let baseUrl = (BUILD_TIME_BASE || FALLBACK_BASE).replace(/\/$/, '')

// 拉取运行时配置。失败不阻塞应用启动，直接用构建期或同源地址。
export async function loadRuntimeConfig() {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' })
    if (!response.ok) return baseUrl
    const config = await response.json()
    if (config?.apiBaseUrl) {
      baseUrl = String(config.apiBaseUrl).replace(/\/$/, '')
    }
  } catch {
    /* 配置缺失是正常情况，保持默认值 */
  }
  return baseUrl
}

export function apiBaseUrl() {
  return baseUrl
}

const VOTER_KEY = 'wenjuan-vue:voter-id'

// 投票者标识在浏览器本地生成并长期保存，后端用它实现"一人一票 + 可改票"。
// 不含任何个人信息，清掉即等于换一个匿名身份。
function voterID() {
  let id = localStorage.getItem(VOTER_KEY)
  if (!id) {
    id = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36)
    localStorage.setItem(VOTER_KEY, id)
  }
  return id
}

/** ApiError 保留 HTTP 状态码与后端的字段级错误，供 UI 精准提示。 */
export class ApiError extends Error {
  constructor(status, code, message, details = []) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

// 后端跑在 Cloudflare Workers 上，边缘冷启动是毫秒级的，
// 不存在 Render 那种"实例休眠后要等一分钟"的问题，所以单一超时就够。
// 10 秒足够覆盖跨洋往返 + D1 查询；再久基本就是真的出问题了，
// 应该快速失败并让用户重试，而不是让界面无限转圈。
const REQUEST_TIMEOUT = 10000

async function request(path, { method = 'GET', body } = {}) {
  // AbortController 给请求加超时，避免网络异常时界面永久转圈。
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

  try {
    const response = await fetch(baseUrl + path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-Voter-Id': voterID()
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal
    })

    if (response.status === 204) return null

    const text = await response.text()
    const payload = text ? JSON.parse(text) : null

    if (!response.ok) {
      const err = payload?.error ?? {}
      throw new ApiError(
        response.status,
        err.code ?? 'unknown',
        err.message ?? `请求失败（HTTP ${response.status}）`,
        err.details ?? []
      )
    }
    return payload
  } catch (error) {
    if (error instanceof ApiError) throw error
    if (error.name === 'AbortError') {
      throw new ApiError(0, 'timeout', '请求超时，请检查网络后重试')
    }
    throw new ApiError(0, 'network_error', '无法连接后端服务，请稍后重试')
  } finally {
    clearTimeout(timer)
  }
}

export const api = {
  listPolls: () => request('/polls').then((r) => r.data),
  getPoll: (pollId) => request(`/polls/${pollId}`).then((r) => r.data),
  createPoll: (title, options) =>
    request('/polls', { method: 'POST', body: { title, options } }).then((r) => r.data),
  vote: (pollId, optionId) =>
    request(`/polls/${pollId}/votes`, { method: 'POST', body: { optionId } }).then((r) => r.data),
  deletePoll: (pollId) => request(`/polls/${pollId}`, { method: 'DELETE' })
}
